import { useState, useEffect } from 'react';
import { useDashboardData as useApiDashboardData } from './useApi';
import { useDashboardSocket } from './useSocket';
import dashboardService from '../services/dashboardService';

export const useDashboardData = (options = {}) => {
  // Use React Query for API data management
  const {
    data: apiData,
    isLoading: apiLoading,
    error: apiError,
    refetch,
  } = useApiDashboardData(options);

  // Use Socket.IO for real-time updates
  const {
    connected: socketConnected,
    metrics: realtimeMetrics,
    activities: realtimeActivities,
  } = useDashboardSocket();

  // Empty defaults when API hasn't loaded yet
  const emptyData = {
    metrics: {
      totalInteractions: 0,
      totalInteractionsChange: 0,
      activeChats: 0,
      activeChatChange: 0,
      responseTime: 0,
      responseTimeChange: 0,
      satisfactionRate: 0,
      satisfactionRateChange: 0,
    },
    chartData: [],
    recentActivities: [],
    widgets: [],
  };

  // Process and merge data from API and real-time updates
  const processedData = () => {
    if (apiError) {
      return emptyData;
    }

    if (!apiData) {
      return emptyData;
    }

    // Transform API data to match expected format
    const transformedData = dashboardService.transformDashboardData(apiData);

    // Merge with real-time updates if available (only if they have actual data)
    const hasRealtimeMetrics = realtimeMetrics && typeof realtimeMetrics === 'object' && !Array.isArray(realtimeMetrics) && Object.keys(realtimeMetrics).length > 0;
    const hasRealtimeActivities = realtimeActivities && Array.isArray(realtimeActivities) && realtimeActivities.length > 0;

    const finalData = {
      ...transformedData,
      metrics: hasRealtimeMetrics ? realtimeMetrics : transformedData.metrics,
      recentActivities: hasRealtimeActivities ? realtimeActivities : transformedData.recentActivities,
    };

    return finalData;
  };

  const data = processedData();
  const loading = apiLoading;

  // Update metrics function - now triggers API refresh
  const updateMetrics = async () => {
    try {
      await refetch();
    } catch (error) {
      // Failed to update metrics
    }
  };

  // Listen for dashboard refresh events
  useEffect(() => {
    const handleDashboardRefresh = () => {
      refetch();
    };

    window.addEventListener('dashboard:refresh', handleDashboardRefresh);
    
    return () => {
      window.removeEventListener('dashboard:refresh', handleDashboardRefresh);
    };
  }, [refetch]);

  return {
    data,
    loading,
    error: apiError,
    updateMetrics,
    refetch,
    socketConnected,
    isRealTime: socketConnected,
  };
};